import React from 'react';

import { getClickType, ClickType } from '../../../../sharedModules/getClickType';
import { getFormattedPrice } from '../../../../sharedModules/getFormattedPrice';
import { getPriceFromDeal } from '../../../../sharedModules/getPriceFromDeal';
import type { Deal } from '../../../types/Deal';
import getPriceType from '../../../utils/getPriceType';
import { useHawkWidgetContext } from '../HawkWidget/HawkWidgetProviderContext';

import DisplayPriceContractType from './DisplayPriceContractType';
import styles from './styles/display-price.css';

interface DisplayPriceProps {
  deal: Deal;
  className?: string;
  type?: string;
  label?: string;
  showContractType?: boolean;
  showOfferLabel?: boolean;
  forceAppear?: boolean;
  wrapInLink?: boolean;
  useCostFreeText?: boolean;
  clickType?: ClickType;
}

const DisplayPrice: React.FC<DisplayPriceProps> = ({
  className,
  showOfferLabel,
  forceAppear,
  deal,
  type,
  label = '',
  showContractType,
  wrapInLink = true,
  useCostFreeText = false,
  clickType,
}) => {
  const {
    translate,
    language,
    showPrice,
    showWasPrice,
    showPriceRounded,
    genericSharedComponents: { AffiliateLink },
    articleUrl,
  } = useHawkWidgetContext();

  // Use the passed type. If not passed use monthly (if a contract) or retail (upfront price)
  type = type || getPriceType(deal);
  showContractType =
    typeof showContractType === 'undefined'
      ? ['monthly', 'yearly'].indexOf(type) >= 0
      : showContractType;

  // Determine the price to display based on the passed type
  const containerStyle = className && styles[className] ? className : 'container';
  const price = getPriceFromDeal(deal, type);
  const formattedPrice = getFormattedPrice({
    price,
    currencyIso: deal.offer.currency_iso,
    showOfferLabel,
    type,
    locale: language,
    translate,
    showPriceRounded,
    useConstFreeText: useCostFreeText,
    label: deal.offer.label,
  });

  /*
    Show the price if showPrice is enabled, and we have a price
    Show a wasPrice if showWasPrice has a valid value, showPrice is enabled and we have a price
  */
  if (
    (showPrice === 'showPrice' ||
      showPrice === 'stream' ||
      showPrice === 'watchAt' ||
      forceAppear) &&
    formattedPrice &&
    (type !== 'wasPrice' ||
      (type === 'wasPrice' &&
        ['price', 'allPercentage', 'allSaving', 'percentagePrice'].indexOf(showWasPrice) >= 0))
  ) {
    const mainComponent = (
      <span
        className={styles[containerStyle]}
        data-type={deal?.offer?.label && showOfferLabel ? 'buySimilar' : type}
      >
        {' '}
        {label && <span className={styles['label']}>{label}</span>}
        <span className={styles['price']}>{formattedPrice}</span>
        {showContractType && (
          <DisplayPriceContractType deal={deal} type={type} className={className} />
        )}
      </span>
    );

    return wrapInLink ? (
      <AffiliateLink
        deal={deal}
        clickType={getClickType({
          link: deal.offer.link,
          articleUrl,
          productType: deal.product_type,
          clickType,
        })}
        areaClicked="price"
        className={className}
      >
        {mainComponent}
      </AffiliateLink>
    ) : (
      mainComponent
    );
  }

  return null;
};

export default DisplayPrice;
